'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';

type Theme = 'light' | 'dark';

interface ThemeContextValue {
  theme: Theme;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'light',
  toggleTheme: () => {},
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }: { children: React.ReactNode }) => {
  const [theme, setTheme] = useState<Theme>('light');
  const [userId, setUserId] = useState<string | null>(null);
  const supabase = createClient();

  // Apply theme class to <html>
  const applyTheme = useCallback((t: Theme) => {
    if (typeof document !== 'undefined') {
      document.documentElement.classList.toggle('dark', t === 'dark');
    }
  }, []);

  // Load theme from Supabase on mount / auth change
  useEffect(() => {
    const loadTheme = async (uid: string) => {
      const { data } = await supabase
        .from('user_profiles')
        .select('theme_preference')
        .eq('id', uid)
        .single();

      const saved = (data?.theme_preference as Theme) || 'light';
      setTheme(saved);
      applyTheme(saved);
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session?.user) {
        setUserId(session.user.id);
        loadTheme(session.user.id);
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUserId(session.user.id);
        loadTheme(session.user.id);
      } else {
        setUserId(null);
        setTheme('light');
        applyTheme('light');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const toggleTheme = useCallback(async () => {
    const next: Theme = theme === 'light' ? 'dark' : 'light';
    setTheme(next);
    applyTheme(next);

    if (userId) {
      await supabase
        .from('user_profiles')
        .update({ theme_preference: next })
        .eq('id', userId);
    }
  }, [theme, userId, applyTheme, supabase]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
